/**
 * BMD NTCS booking import ("Import Buchungen", the BuErf format for third-party systems).
 *
 * A semicolon file with a header row - BMD maps columns by name - decimal comma, dates as
 * TT.MM.JJJJ, CR/LF, Windows-1252. The sign rules follow the vendors that publish their BMD
 * interface (dvo, Cosmoconsult, EDV Hausleitner), since BMD hands its own spec only to partners:
 *
 *  - ER and AR lead with the Personenkonto, so `betrag` is gross and `steuer` carries the
 *    opposite sign: ER `buchcode` 2 with -120,00 / 20,00, AR `buchcode` 1 with 1440,00 / -240,00.
 *  - A Barbeleg leads with the expense account against the cash (or card) account, so `betrag`
 *    is net and `steuer` has the same sign: 36,36 / 3,64 at 10 %.
 *  - Several rates on one Beleg become one row per rate with the same `belegnr`, which BMD keeps
 *    as one open item per row of the same number. A tip is a row without tax.
 *  - Reverse charge and ig. Erwerb leave `steuer` empty; BMD computes it from the tax code.
 */
import type { DocumentRecord } from "@/lib/api/documents";
import { relevantDate } from "@/lib/at/uva";
import { money } from "@/lib/export/tables";
import {
  clean,
  dateParts,
  decimal,
  numericBelegnr,
  prepareBookings,
  type BookingContext,
  type BookingProblem,
} from "./common";
import { bmdTaxCode } from "./settings";
import type { PlainBooking } from "./money-bookings";

export type { BookingProblem } from "./common";
export type BmdContext = BookingContext;

export const BMD_COLUMNS = [
  "satzart",
  "konto",
  "gkonto",
  "belegnr",
  "belegdatum",
  "buchdatum",
  "buchsymbol",
  "buchcode",
  "prozent",
  "steuercode",
  "betrag",
  "steuer",
  "text",
  "extbelegnr",
  "uidnr",
  "dokument",
] as const;

/** "2026-03-14 00:00:00.000Z" -> "14.03.2026". */
export function bmdDate(value: string | undefined): string {
  const parts = dateParts(value);
  return parts ? parts.join(".") : "";
}

export function bmdBookings(
  documents: DocumentRecord[],
  context: BmdContext,
): { rows: string[][]; problems: BookingProblem[] } {
  const { bookable, problems } = prepareBookings(documents, context);
  const rows: string[][] = [];

  for (const { document, account, counterAccount, lines, tip } of bookable) {
    const personal = document.kind !== "KA";
    const outgoing = document.kind === "AR";
    const treatment = document.taxTreatment ?? "standard";
    const code = bmdTaxCode(context.settings, `${outgoing ? "out" : "in"}.${treatment}`);
    const shared = [
      numericBelegnr(document),
      bmdDate(document.issueDate),
      bmdDate(relevantDate(document, context.basis) || document.issueDate),
      document.kind,
    ];
    const tail = [
      clean(`${document.docNumber ?? ""} ${document.partnerName ?? ""}`, 40),
      clean(document.invoiceNumber || document.docNumber || "", 35),
      clean(document.partnerVatId ?? "", 20),
      context.documentPath(document),
    ];
    const booked = [
      ...lines.map((line) => ({ ...line, code })),
      ...(tip > 0 ? [{ rate: 0, net: tip, vat: 0, code: "" }] : []),
    ];

    for (const line of booked) {
      const gross = line.net + line.vat;
      // ER/AR lead with the Personenkonto: gross, tax with the opposite sign. A Barbeleg leads
      // with the expense account against cash or card: net, tax with the same sign.
      const [konto, gkonto] = personal ? [counterAccount, account] : [account, counterAccount];
      const buchcode = personal && !outgoing ? "2" : "1";
      const betrag = personal ? (outgoing ? gross : -gross) : line.net;
      const steuer = personal && !outgoing ? line.vat : outgoing ? -line.vat : line.vat;
      rows.push([
        "0",
        konto,
        gkonto,
        ...shared,
        buchcode,
        decimal(line.rate),
        line.code,
        money(betrag),
        line.vat !== 0 ? money(steuer) : "",
        ...tail,
      ]);
    }
  }
  return { rows, problems };
}

/** The file BMD imports: header row, ";" between fields, CR/LF. Encode with `encodeCp1252`. */
export function bmdCsv(rows: string[][]): string {
  return [BMD_COLUMNS.join(";"), ...rows.map((row) => row.join(";"))].join("\r\n") + "\r\n";
}

export const BMD_PARTNER_COLUMNS = ["konto", "name", "adresse", "uidnr", "iban", "art"] as const;

/** Personenkonten for "Import PK variabel": mapped once in NTCS, so plain names suffice. */
export function bmdPartnerCsv(
  accounts: Array<{
    number: number;
    name?: string;
    address?: string;
    vatId?: string;
    iban?: string;
    role: "supplier" | "customer";
  }>,
): string {
  const rows = [...accounts]
    .sort((a, b) => a.number - b.number)
    .map((account) =>
      [
        String(account.number),
        clean(account.name ?? "", 80),
        clean(account.address ?? "", 120),
        clean(account.vatId ?? "", 20),
        clean(account.iban ?? "", 34),
        account.role === "supplier" ? "Kreditor" : "Debitor",
      ].join(";"),
    );
  return [BMD_PARTNER_COLUMNS.join(";"), ...rows].join("\r\n") + "\r\n";
}

/**
 * Bookings between two Sachkonten (`moneyBookings`): debit account first, a positive amount,
 * buchcode 1, no tax code - none of them carries VAT.
 */
export function bmdPlainRows(bookings: PlainBooking[]): string[][] {
  return bookings.map((booking) => [
    "0",
    booking.debit,
    booking.credit,
    booking.belegnr,
    bmdDate(booking.date),
    bmdDate(booking.date),
    booking.symbol,
    "1",
    "",
    "",
    money(booking.amount),
    "",
    clean(booking.text, 40),
    "",
    "",
    "",
  ]);
}
